import React from 'react';
import { Col } from 'reactstrap';
import { faToolbox } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    GroupedRow,
    Input,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { Can } from '../Can';
import { Equipment } from './Equipment';
import { EquipmentLongTermStatusForm } from './EquipmentLongTermStatusForm';
import { EquipmentLongTermStatusInfo } from './EquipmentLongTermStatusInfo';

export class EquipmentForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.equipmentLongTermStatusFormRef = React.createRef();

        const stateBase = {
            equipment: this.props.equipment ?? new Equipment(),
            equipmentTypes: [],
            dispatchLocations: [],
            equipmentStatuses: [],
            employeesAssignable: [],
            equipmentAssignable: [],
            equipmentAssignableTypes: [],
            equipmentTags: [],
            showEmployeeAssignment: false,
            showEquipmentAssignment: false,
            showEquipmentLongTermStatusForm: false,
            selectedEquipmentLongTermStatus: null,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.equipmentTagsOnChange = this.equipmentTagsOnChange.bind(this);
    }

    componentDidMount() {
        const { show } = this.props;

        if (show) {
            this.open(this.props.selectedEquipmentId);
        }
    }

    componentDidUpdate(prevProps) {
        const becameVisible = !prevProps.show && this.props.show;
        const idChanged = prevProps.selectedEquipmentId !== this.props.selectedEquipmentId;
        if (this.props.show && (becameVisible || idChanged)) {
            this.open(this.props.selectedEquipmentId);
        }
    }

    onChange = onFieldChange;
    setStateAsync = (state) => new Promise((resolve) => {
            this.setState(state, resolve);
        });
    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onDelete = async () => {
        const { equipment } = this.state;
        const response = await httpClient.delete(
            ApiRoutes.equipment.delete(equipment.id),
        );
        if (response) {
            this.toggleShow(false);
            this.onClose(response);
        }
    };

    toggleEquipmentLongTermStatusForm = (show) => this.setState({
        showEquipmentLongTermStatusForm: show,
    });

    onSaveLongTermStatus = async () => {
        const { equipment } = this.state;

        const equipmentLongTermStatuses = await httpClient.js(
            ApiRoutes.equipmentLongTermStatus.allForEquipment(equipment.id),
        );

        this.setState({
            equipmentLongTermStatuses,
            showEquipmentLongTermStatusForm: false,
            selectedEquipmentLongTermStatus: null,
        });

        this.context.setFormOpened(false);
    };

    onAddEquipmentLongTermStatus = () => {
        const { equipment } = this.state;

        this.equipmentLongTermStatusFormRef.current.open(equipment.id);
        this.setState({
            showEquipmentLongTermStatusForm: true,
            equipmentId: equipment.id,
            selectedEquipmentLongTermStatus: null,
        });
    };

    onEditEquipmentLongTermStatus = (suppliedLongTermStatus) => {
        if (suppliedLongTermStatus.id) {
            this.equipmentLongTermStatusFormRef.current.open(
                suppliedLongTermStatus,
            );
            this.setState({
                showEquipmentLongTermStatusForm: true,
            });
        }
    };

    onDispatchLocationChanged = async (selectedItem) => {
        const { equipment, equipmentAssignableTypes } = { ...this.state };
        let employeesAssignable = [];
        let equipmentAssignable = [];

        equipment.dispatchLocationId = selectedItem == null ? '' : selectedItem.value ?? '';

        if (equipment.dispatchLocationId > 0 && equipment.equipmentTypeId > 0) {
            const [dispatchEmployees] = await Promise.all([
                httpClient.get(
                    ApiRoutes.employees.byDispatchLocationAndEquipmentType(
                        equipment.dispatchLocationId,
                        equipment.equipmentTypeId,
                    ),
                ),
            ]);

            // Only allow assignment to assignable types as retrieved from equipment type dropdown cascade
            const dispatchEquipment = [
                ...equipmentAssignableTypes.filter(
                    (x) => x.dispatchLocationId === equipment.dispatchLocationId,
                ),
            ];

            employeesAssignable = dispatchEmployees ?? [];
            equipmentAssignable = dispatchEquipment ?? [];
        }

        this.setState({
            equipment,
            employeesAssignable,
            equipmentAssignable,
        });
    };

    onEquipmentTypeChanged = async (selectedItem) => {
        const eq = this.state.equipment;
        let employeeAssignment = false;
        let equipmentAssignment = false;
        const employeesAssignable = [];
        let equipmentAssignableTypes = [];
        const hasGroups = !!((selectedItem ?? {}).groups ?? []).length;
        const hasRequiredEquipment = !!((selectedItem ?? {}).requiredEquipment ?? []).length > 0;

        // Check max assigned

        employeeAssignment = !!hasGroups && !hasRequiredEquipment;
        equipmentAssignment = !hasGroups && !!hasRequiredEquipment;

        if (hasGroups) {
            // let data = await httpClient.post(ApiRoutes.employees.byGroups(), selectedItem.groups);
            // employeesAssignable = data ?? [];
        }

        if (hasRequiredEquipment) {
            const [type] = selectedItem.requiredEquipment.map((e) => e.id);
            const data = await httpClient.js(ApiRoutes.equipment.byType(type));
            equipmentAssignableTypes = data ?? [];
        }

        eq.equipmentTypeId = selectedItem == null ? '' : selectedItem.id ?? '';
        eq.dispatchLocationId = null; // reset the dispatch location, to reset cascade options/assignable equipment.

        await this.setStateAsync({
            equipment: eq,
            showEmployeeAssignment: employeeAssignment,
            showEquipmentAssignment: equipmentAssignment,
            employeesAssignable,
            equipmentAssignableTypes,
            isGeneric: selectedItem ? selectedItem.isGeneric : true,
            equipmentAssignable: [], // reset assignable equipment awaiting dispatch location selection
        });
    };

    onSelectChange = onReactSelectChanged;
    equipmentTagsOnChange = (e) => {
        const { equipment } = this.state;
        equipment.equipmentTags = e ?? [];
        this.setState({ equipment });
    };

    onSubmit = async () => {
        const { equipment } = this.state;
        this.setState({ errors: {}, saving: true });

        // Is this POST or PUT?
        const url = equipment.id
            ? ApiRoutes.equipment.update(equipment.id)
            : ApiRoutes.equipment.create();
        const fetch_cte = equipment.id ? httpClient.put : httpClient.post;

        const response = await fetch_cte(url, equipment).catch(
            this.handleSaveError,
        );

        if (response) {
            this.onClose();
            toasty.success(
                'Equipment Saved',
                `Equipment [${equipment.description}] saved successfully.`,
            );
        }

        this.setState({ saving: false });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (equipmentId) => {
        this.setState({ loading: true });

        const stateUpdate = await this.populateState(equipmentId);

        let equipment = null;

        this.resetForm();

        if (equipmentId) {
            equipment = await httpClient.js(
                ApiRoutes.equipment.byId(equipmentId),
            );
        } else {
            equipment = new Equipment();
        }

        this.setState({ equipment }, async () => {
            this.props.toggleShow(true);
            this.context.setFormOpened(true);

            if (equipmentId) {
                // step through cascaded options on the way in
                const { equipmentTypes, dispatchLocations } = stateUpdate;
                const eqType = equipmentTypes.find(
                    (x) => x.id === equipment.equipmentTypeId,
                );
                const dispatchLoc = dispatchLocations.find(
                    (x) => x.value === equipment.dispatchLocationId,
                );

                // These need to be triggered in this order, otherwise the dispatchLocationId may get nulled erroneously
                // TODO This conflicting logic should be fixed
                await this.onEquipmentTypeChanged({ ...eqType });
                await this.onDispatchLocationChanged({ ...dispatchLoc });
            }

            this.setState({ loading: false });
        });
    };

    async populateState(equipmentId) {
        const [equipmentTypes, dispatchLocations, equipmentStatuses, equipmentTags, equipmentLongTermStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.equipmentType.all()),
            httpClient.js(ApiRoutes.typeAheads.dispatchCompaniesForEquipment(equipmentId)),
            httpClient.js(ApiRoutes.typeAheads.equipmentTypeStatuses()),
            httpClient.js(ApiRoutes.equipmentTags.all()),
            equipmentId > 0
                ? httpClient.js(ApiRoutes.equipmentLongTermStatus.allForEquipment(equipmentId))
                : [],
        ]);

        const state = {
            equipmentTypes,
            dispatchLocations,
            equipmentStatuses,
            equipmentTags,
            equipmentLongTermStatuses,
        };

        this.setState(state);

        return state;
    }

    resetForm = () => {
        this.setState({
            showEmployeeAssignment: false,
            showEquipmentAssignment: false,
            formValidated: false,
        });
    };

    render() {
        const { tenantSettings } = { ...this.context.tenant };

        const {
            equipmentTypes,
            dispatchLocations,
            formValidated,
            equipmentStatuses,
            validationMessage,
            employeesAssignable,
            equipmentAssignable,
            isGeneric,
            equipmentTags,
            showEquipmentLongTermStatusForm,
            selectedEquipmentLongTermStatus,
            equipmentLongTermStatuses,
            saving,
        } = this.state;

        const eq = this.state.equipment;
        const enableEquipmentTags = Boolean(
            tenantSettings
            && tenantSettings.enableEquipmentTags,
        );

        const selectableEquipmentTypes = equipmentTypes.filter(
            (t) => !(t.isSyncEnabled && t.assetTypeExternalId)
                || t.id === eq.equipmentTypeId,
        );

        return (
            <>
                <SlideForm
                    loading={this.state.loading}
                    show={this.props.show}
                    id="equipmentForm"
                    formIcon={faToolbox}
                    formTitle={
                        (!!parseInt(eq.id ?? 0) > 0 || this.props.selectedEquipmentId)
                            ? 'Edit Equipment'
                            : 'Add Equipment'
                    }
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    onSave={this.onSave}
                    onDelete={this.onDelete}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    saving={saving}
                >
                    <ValidatedSelect
                        id="equipmentTypeId"
                        name="equipmentTypeId"
                        label="Equipment Type"
                        required
                        disabled={!eq.canEdit}
                        options={selectableEquipmentTypes}
                        value={
                            equipmentTypes.find(
                                (i) => i.id === eq.equipmentTypeId,
                            ) ?? ''
                        }
                        getOptionLabel={(option) => option.description}
                        getOptionValue={(option) => option.id}
                        onChange={this.onEquipmentTypeChanged}
                        error="The equipment type is required."
                    />

                    <Input
                        label="Number"
                        id="identifier"
                        name="equipment.identifier"
                        value={eq.identifier ?? ''}
                        disabled={!eq.canEdit}
                        onChange={this.onChange}
                        placeholder="Enter number"
                        error="Number is required."
                        required
                    />
                    <Input
                        label="Description"
                        id="description"
                        name="equipment.description"
                        value={eq.description ?? ''}
                        disabled={!eq.canEdit}
                        onChange={this.onChange}
                        placeholder="Enter description"
                        error="Description is required."
                        required
                    />

                    <ValidatedSelect
                        id="dispatchLocationId"
                        name="equipment.dispatchLocationId"
                        label="Dispatching"
                        required
                        disabled={!eq.canEdit}
                        options={dispatchLocations}
                        value={
                            dispatchLocations.find(
                                (i) => i.value === eq.dispatchLocationId,
                            ) ?? ''
                        }
                        onChange={this.onDispatchLocationChanged}
                        error="Dispatching Office is required."
                    />

                    <ValidatedSelect
                        id="equipmentStatusId"
                        name="equipmentStatusId"
                        label="Status"
                        required
                        disabled={!eq.canEdit}
                        options={equipmentStatuses}
                        value={
                            equipmentStatuses.find(
                                (i) => i.value === eq.equipmentStatusId,
                            ) ?? ''
                        }
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        onChange={(sel) => {
                            const value = (sel ?? {}).value ?? '';
                            this.setState(
                                (state) => (
                                    (state.equipment.equipmentStatusId = value),
                                    state
                                ),
                            );
                        }}
                        error="Status is required."
                    />

                    {this.state.showEmployeeAssignment && (
                        <ValidatedSelect
                            id="employeeAssignmentId"
                            name="employeeAssignmentId"
                            label="Employee Assigned"
                            options={employeesAssignable}
                            value={
                                employeesAssignable.find(
                                    (i) => i.id === eq.employeeAssignedId,
                                ) ?? ''
                            }
                            getOptionLabel={(option) => option.fullName}
                            getOptionValue={(option) => option.id}
                            onChange={(sel) => {
                                const value = (sel ?? {}).id ?? '';
                                this.setState(
                                    (state) => (
                                        (state.equipment.employeeAssignedId = value),
                                        state
                                    ),
                                );
                            }}
                        />
                    )}

                    {this.state.showEquipmentAssignment && (
                        <ValidatedSelect
                            id="equipmentAssignmentId"
                            name="equipmentAssignmentId"
                            label="Equipment Assigned"
                            isDisabled={
                                !(this.state.equipment ?? {}).dispatchLocationId
                            }
                            options={equipmentAssignable}
                            value={
                                equipmentAssignable.find(
                                    (i) => i.id === eq.equipmentAssignedId,
                                ) ?? ''
                            }
                            getOptionLabel={(option) => option.description}
                            getOptionValue={(option) => option.id}
                            onChange={(sel) => {
                                const value = (sel ?? {}).id ?? '';
                                this.setState(
                                    (state) => (
                                        (state.equipment.equipmentAssignedId = value),
                                        state
                                    ),
                                );
                            }}
                        />
                    )}

                    {!isGeneric && (
                        <Input
                            label="Serial No."
                            id="equipmentSerialNo"
                            name="equipment.serial"
                            value={eq.serial ?? ''}
                            onChange={this.onChange}
                            placeholder="Equipment Serial No."
                            disabled={!eq.canEdit}
                        />
                    )}

                    {enableEquipmentTags && (
                        <ValidatedSelect
                            id="equipmentTagId"
                            name="equipment.equipmentTags"
                            label="Equipment Tags"
                            isMulti
                            options={equipmentTags}
                            value={(equipmentTags ?? []).filter((x) =>
                                (eq.equipmentTags ?? []).some((t) => t.id === x.id),
                            )}
                            getOptionLabel={(option) => option.tag}
                            getOptionValue={(option) => option.id}
                            onChange={this.equipmentTagsOnChange}
                        />
                    )}

                    <Input
                        label="NetSuite Asset Id"
                        id="externalId"
                        name="equipment.externalId"
                        value={eq.externalId ?? ''}
                        disabled={!eq.canEdit}
                        onChange={this.onChange}
                    />

                    {!isGeneric && (
                        <Can
                            I="view"
                            a="equipmentlongtermstatus"
                        >
                            <SubHeading>
                                Long-Term Statuses
                            </SubHeading>

                            <GroupedRow>
                                <Col sm="12">
                                    <EquipmentLongTermStatusInfo
                                        onAddEquipmentLongTermStatus={this.onAddEquipmentLongTermStatus}
                                        equipmentLongTermStatuses={equipmentLongTermStatuses}
                                        onEditEquipmentLongTermStatus={this.onEditEquipmentLongTermStatus}
                                    />
                                </Col>
                            </GroupedRow>
                        </Can>
                    )}
                </SlideForm>

                <Can
                    do="edit"
                    on="equipmentlongtermstatus"
                >
                    <EquipmentLongTermStatusForm
                        onClose={this.onSaveLongTermStatus}
                        ref={this.equipmentLongTermStatusFormRef}
                        show={showEquipmentLongTermStatusForm}
                        toggleShow={
                            this.toggleEquipmentLongTermStatusForm
                        }
                        employeeLongTermStatus={
                            selectedEquipmentLongTermStatus
                        }
                        onSaveCallback={this.onSaveLongTermStatus}
                    />
                </Can>
            </>
        );
    }
}